import { useQuery } from '@apollo/client';
import { APIErrorCodes } from 'app-types';
import { Notification } from 'Components';
import { usePrevious } from 'Hooks/usePrevious';
import { useStores } from 'Hooks/useStore';
import { isEqual } from 'lodash';
import { observer } from 'mobx-react-lite';
import { AddUserValues, EditUserValues, PrimarySystemRole } from 'Pages/User/User.types';
import { useMemo, useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_JOB_CLASSES } from 'Services/Api/Masterdata/Queries';
import { GetJobClassesResponse } from 'Services/Api/Masterdata/Types';
import { getIsModuleSettingsDisabled } from './ModuleSettings.helpers';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { Group } from 'Services/Api/Groups/Types';
import { useFormikContext } from 'formik';
import { getCustomTenantRoles, getUserTenantRoles } from 'Pages/User/service/index.Service';

interface ModuleSettingsContainerProps {
	values: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>;
	onDisabled: () => void;
	children: (disabled: boolean) => React.ReactNode;
}

export function useIsModuleSettingDisabled(
	values?: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>
) {
	const { authStore } = useStores();
	const disabled = useMemo(() => {
		return values
			? getIsModuleSettingsDisabled(
					values,
					authStore.currentUserPermissionCodes
			  )
			: true;
	}, [values, authStore.currentUserPermissionCodes]);

	const getIsLMSModuleSettingsDisabled = (
		values: Pick<EditUserValues, 'primarySystemRoleId' | 'groups'>
	) => {
		return getIsModuleSettingsDisabled(
			values,
			authStore.currentUserPermissionCodes
		);
	};
	return { disabled, getIsLMSModuleSettingsDisabled };
}

interface UseJobClassProps {
	values: Pick<EditUserValues, 'groups' | 'moduleSettings'> & {
		primarySystemRoleId?:
			| EditUserValues['primarySystemRoleId']
			| PrimarySystemRole;
	};
}
export function useJobClass({ values }: UseJobClassProps) {
  
	const shouldSkipRequest = useMemo(() => {
		if (Array.isArray(values?.primarySystemRoleId)) {
			const systemRole = values.primarySystemRoleId.find(
				(role) => role.data.isPrimary
			);
			const role = systemRole?.data?.role;
      if(role === SystemRoleType.SuperAdmin ||
				role === SystemRoleType.Owner) return true
		}
    if( !Array.isArray(values?.groups) || !values.groups.length) return true
		return false;
	}, [values?.primarySystemRoleId, values?.groups]);
	const { t } = useTranslation();
	const groupIds = useMemo(() => (values.groups ?? []).map((d) => d.key), [
		values.groups
	]);
	const { called, loading, data, refetch } = useQuery<GetJobClassesResponse>(
		GET_JOB_CLASSES,
		{
			fetchPolicy: 'cache-first',
			skip: shouldSkipRequest,
			variables: {
				groupIds
			}
		}
	);

	const fetch = async () => {
		try {
			if (shouldSkipRequest) return;
			await refetch({
				variables: {
					groupIds
				}
			});
		} catch (e) {
			if (groupIds.length) {
				Notification.error({
					description: t(
						`user.errorCodes.${APIErrorCodes.UserGroupInTheSameHierarchy}`
					)
				});
			} else {
				Notification.error({
					description: t('errorCodes.genericErrorMessage')
				});
			}
		}
	};

	useEffect(() => {
		fetch();
		// eslint-disable-next-line
	}, [groupIds]);

	return {
		loading: !called || loading,
		data: data?.getJobClassesList
	};
}

interface UseResetLmsFieldsOnGroupsChangeProps {
	values: Pick<EditUserValues, 'groups' | 'moduleSettings'>;
	resetLmsFields: () => void;
}
export const useResetLmsFieldsOnGroupsChange = ({
	values,
	resetLmsFields
}: UseResetLmsFieldsOnGroupsChangeProps) => {
	const previousValues = usePrevious(values);
	useEffect(() => {
		const isModuleSettingsChanged = !isEqual(
			previousValues?.moduleSettings,
			values.moduleSettings
		);
		if (
			previousValues?.groups &&
			!isEqual(values.groups, previousValues.groups) &&
			!isModuleSettingsChanged
		) {
			resetLmsFields();
		}
		// eslint-disable-next-line
	}, [
		values.groups,
		values.moduleSettings,
		previousValues?.groups,
		previousValues?.moduleSettings
	]);
};

export const useUserTenantRoles = (
	peakV3Id?: string,
	enabled = false
) => {
	const { setFieldValue, resetForm, values } = useFormikContext<EditUserValues>();

	useEffect(() => {
		if (!enabled || !peakV3Id) return;
		const fetch = async () => {
			try {
				const data = await getUserTenantRoles(peakV3Id);
				resetForm({
					values: {
						...values,
						customTenantRoleIds: data.tenantRoles.map((r: any) => r.roleId)
					}
				});
				setFieldValue('customTenantRoleIds',
					data.tenantRoles.map((r: any) => r.roleId));
			} catch (e) {
				console.error(e);
			}
		};
		fetch();
	}, [peakV3Id, enabled, setFieldValue]);
};

export const useCustomTenantRolesDropdownList = (
	groups: AddUserValues['groups']
) => {
	const [
		customTenantRolesDropdownList,
		setCustomTenantRolesDropdownList
	] = useState<Group[]>([]);

	const { setFieldValue } =
		useFormikContext<AddUserValues>();

	const previousGroups = useRef(groups);

	useEffect(() => {
		const fetchRoles = async () => {

			if (!groups?.length) {
				setCustomTenantRolesDropdownList([]);
				return;
			}

			if (previousGroups.current !== groups) {
				setFieldValue('customTenantRoleIds', []);
				previousGroups.current = groups;
			}

			try {
				const tenantId = groups[0].key;
				const data = await getCustomTenantRoles(tenantId);
				setCustomTenantRolesDropdownList(
					(data.data || []).map((role: any) => ({
						id: role.id,
						name: role.roleName,
						description: role.description,
					}))
				);
			} catch {
				Notification.error({
					description: 'Failed to load custom tenant roles.',
				});
				setCustomTenantRolesDropdownList([]);
			}
		};

		fetchRoles();
	}, [groups, setFieldValue]);

	return customTenantRolesDropdownList;
};

function ModuleSettingsContainer({
	values,
	onDisabled,
	children
}: ModuleSettingsContainerProps) {
	const { disabled } = useIsModuleSettingDisabled(values);

	const previousDisabled = usePrevious(disabled);

	useEffect(() => {
		if (previousDisabled === false && disabled) {
			onDisabled();
		}
	}, [previousDisabled, disabled, onDisabled]);

	return <>{children(disabled)}</>;
}

export default observer(ModuleSettingsContainer);
